import React, { useState } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import {
  REGISTER_COLLEGE_ADMIN, LOGIN_COLLEGE_ADMIN, MY_ADMIN_PROFILE,
  MY_CAMPUS_NEWS, ADMIN_ANNOUNCEMENTS,
  POST_CAMPUS_NEWS, UPDATE_CAMPUS_NEWS, DELETE_CAMPUS_NEWS, PIN_CAMPUS_NEWS,
  POST_ADMIN_ANNOUNCEMENT, DELETE_ADMIN_ANNOUNCEMENT, UPDATE_ADMIN_PROFILE,
} from '../graphql';
import {
  Shield, Plus, X, Edit2, Trash2, Pin, PinOff, Megaphone, Newspaper,
  LogOut, Building2, Users, Eye, Calendar, AlertTriangle, Info, Sun,
  BookOpen, Trophy, Briefcase, Music, GraduationCap, Save, ChevronDown,
  ChevronUp, Bell, BarChart3, MapPin,
} from 'lucide-react';

const ADMIN_KEY   = 'cc_admin_token';
const ADMIN_DATA  = 'cc_admin_data';

const ROLES = [
  { value: 'BRANCH_ADMIN',  label: 'Branch Admin',  desc: 'Manage one branch' },
  { value: 'COLLEGE_ADMIN', label: 'College Admin',  desc: 'All branches in college' },
  { value: 'SUPER_ADMIN',   label: 'Super Admin',    desc: 'All colleges' },
];

const NEWS_CATS = [
  { value: 'GENERAL',    label: 'General',    icon: Info,         color: 'text-blue-600   bg-blue-50' },
  { value: 'ACADEMIC',   label: 'Academic',   icon: BookOpen,     color: 'text-brand-600  bg-brand-50' },
  { value: 'EVENTS',     label: 'Events',     icon: Calendar,     color: 'text-brand-700  bg-brand-50' },
  { value: 'SPORTS',     label: 'Sports',     icon: Trophy,       color: 'text-green-600  bg-green-50' },
  { value: 'PLACEMENT',  label: 'Placement',  icon: Briefcase,    color: 'text-orange-600 bg-orange-50' },
  { value: 'CULTURAL',   label: 'Cultural',   icon: Music,        color: 'text-brand-600  bg-brand-50' },
  { value: 'EXAM',       label: 'Exam',       icon: GraduationCap,color: 'text-red-600    bg-red-50' },
  { value: 'HOLIDAY',    label: 'Holiday',    icon: Sun,          color: 'text-yellow-600 bg-yellow-50' },
];

const ANN_TYPES = [
  { value: 'GENERAL',      label: 'General',      color: 'text-blue-700   bg-blue-100' },
  { value: 'CANCELLATION', label: 'Cancellation', color: 'text-red-700    bg-red-100' },
  { value: 'EXAM_CHANGE',  label: 'Exam Change',  color: 'text-orange-700 bg-orange-100' },
  { value: 'HOLIDAY',      label: 'Holiday',      color: 'text-green-700  bg-green-100' },
  { value: 'URGENT',       label: 'Urgent',       color: 'text-red-800    bg-red-200' },
];

const catCfg = (v) => NEWS_CATS.find(c => c.value === v) || NEWS_CATS[0];

// ── Auth Panel ──────────────────────────────────────────────────────────────
function AuthPanel({ onAuth }) {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ name:'', email:'', password:'', college:'', branch:'', role:'BRANCH_ADMIN', designation:'' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const [loginAdmin,    { loading: ll }] = useMutation(LOGIN_COLLEGE_ADMIN);
  const [registerAdmin, { loading: rl }] = useMutation(REGISTER_COLLEGE_ADMIN);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let res;
      if (mode === 'login') {
        const { data } = await loginAdmin({ variables: { email: form.email, password: form.password } });
        res = data.loginCollegeAdmin;
      } else {
        const { data } = await registerAdmin({ variables: { ...form } });
        res = data.registerCollegeAdmin;
      }
      localStorage.setItem(ADMIN_KEY,  res.token);
      localStorage.setItem(ADMIN_DATA, JSON.stringify(res.admin));
      onAuth(res.token, res.admin);
    } catch (err) { alert(err.message); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: '#188038' }}>
            <Shield size={30} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">College Admin Portal</h1>
          <p className="text-gray-500 text-sm mt-1">Post announcements &amp; campus news for your college / branch</p>
        </div>

        <div className="card p-6">
          <div className="flex rounded-xl overflow-hidden border border-gray-200 mb-5">
            {['login','register'].map(m => (
              <button key={m} onClick={() => setMode(m)}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors ${mode===m ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                {m === 'login' ? 'Sign In' : 'Register'}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === 'register' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Full Name *</label>
                  <input className="input" required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Dr. Ramesh Kumar" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Designation</label>
                  <input className="input" value={form.designation} onChange={e => set('designation', e.target.value)} placeholder="HOD-CSE, Dean Academics, Principal…" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">College *</label>
                  <input className="input" required value={form.college} onChange={e => set('college', e.target.value)} placeholder="SRM Institute of Science and Technology" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Branch (leave empty for college-wide)</label>
                  <input className="input" value={form.branch} onChange={e => set('branch', e.target.value)} placeholder="CSE, ECE, ME… (blank = all branches)" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Admin Role</label>
                  <div className="space-y-2">
                    {ROLES.map(r => (
                      <label key={r.value} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${form.role===r.value ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <input type="radio" name="role" value={r.value} checked={form.role===r.value} onChange={() => set('role', r.value)} className="text-teal-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-800">{r.label}</p>
                          <p className="text-xs text-gray-500">{r.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
              <input type="email" className="input" required value={form.email} onChange={e => set('email', e.target.value)} placeholder="admin@srm.edu.in" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Password *</label>
              <input type="password" className="input" required value={form.password} onChange={e => set('password', e.target.value)} placeholder="••••••••" />
            </div>
            <button type="submit" disabled={ll||rl} className="w-full py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium transition-colors mt-1">
              {(ll||rl) ? 'Please wait…' : mode==='login' ? 'Sign In' : 'Create Admin Account'}
            </button>
          </form>
        </div>
        <p className="text-xs text-gray-400 text-center mt-4">Admin accounts are verified by the institution before activation</p>
      </div>
    </div>
  );
}

// ── Post News Modal ──────────────────────────────────────────────────────────
function PostNewsModal({ existing, onClose }) {
  const [form, setForm] = useState({
    title: existing?.title || '',
    body:  existing?.body  || '',
    category: existing?.category || 'GENERAL',
    imageUrl: existing?.imageUrl || '',
    tags: existing?.tags?.join(', ') || '',
    pinned: existing?.pinned || false,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const [postNews,   { loading: pl }] = useMutation(POST_CAMPUS_NEWS,   { refetchQueries: ['MyCampusNews'] });
  const [updateNews, { loading: ul }] = useMutation(UPDATE_CAMPUS_NEWS, { refetchQueries: ['MyCampusNews'] });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const vars = { ...form, tags: form.tags.split(',').map(t=>t.trim()).filter(Boolean), imageUrl: form.imageUrl||null };
    try {
      if (existing) await updateNews({ variables: { newsId: existing.id, ...vars } });
      else          await postNews({ variables: vars });
      onClose();
    } catch (err) { alert(err.message); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Newspaper size={20} className="text-teal-600" />
            {existing ? 'Edit News' : 'Post Campus News'}
          </h2>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Category pills */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Category</label>
            <div className="flex flex-wrap gap-2">
              {NEWS_CATS.map(c => {
                const Icon = c.icon;
                return (
                  <button key={c.value} type="button" onClick={() => set('category', c.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${form.category===c.value ? c.color+' border-current' : 'text-gray-500 border-gray-200 hover:border-gray-300'}`}>
                    <Icon size={12}/>{c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Headline *</label>
            <input className="input" required value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Annual Tech Fest 'Revels 2025' Registration Open" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Content *</label>
            <textarea className="input" rows={6} required value={form.body} onChange={e => set('body', e.target.value)} placeholder="Full news content, details, important dates, links…" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Cover Image URL</label>
            <input className="input" value={form.imageUrl} onChange={e => set('imageUrl', e.target.value)} placeholder="https://…/image.jpg" />
            {form.imageUrl && (
              <div className="mt-1.5 h-24 rounded-lg overflow-hidden bg-gray-100">
                <img src={form.imageUrl} alt="preview" className="w-full h-full object-cover" onError={e=>{e.target.style.display='none'}} />
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
            <input className="input" value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="exam, registration, deadline" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.pinned} onChange={e => set('pinned', e.target.checked)} className="rounded text-teal-600" />
            <span className="text-sm text-gray-700">Pin this post (shows at top)</span>
          </label>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={pl||ul} className="flex-1 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium transition-colors">
              {(pl||ul) ? 'Saving…' : existing ? 'Save Changes' : 'Publish News'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Post Announcement Modal ──────────────────────────────────────────────────
function PostAnnouncementModal({ onClose }) {
  const [form, setForm] = useState({ title:'', body:'', type:'GENERAL', subject:'', pinned:false });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [postAnn, { loading }] = useMutation(POST_ADMIN_ANNOUNCEMENT, { refetchQueries: ['AdminAnnouncements'] });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try { await postAnn({ variables: form }); onClose(); }
    catch (err) { alert(err.message); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Megaphone size={20} className="text-orange-500" /> Post Announcement
          </h2>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          {/* Type pills */}
          <div className="flex flex-wrap gap-2">
            {ANN_TYPES.map(t => (
              <button key={t.value} type="button" onClick={() => set('type', t.value)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${form.type===t.value ? t.color+' border-current' : 'text-gray-500 border-gray-200'}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Title *</label>
            <input className="input" required value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Mid-term exams rescheduled — Week 9" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Message *</label>
            <textarea className="input" rows={4} required value={form.body} onChange={e => set('body', e.target.value)} placeholder="Full announcement text…" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Subject (optional)</label>
            <input className="input" value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="Affects which subject?" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.pinned} onChange={e => set('pinned', e.target.checked)} className="rounded text-teal-600" />
            <span className="text-sm text-gray-700">Pin this announcement</span>
          </label>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-medium transition-colors">
              {loading ? 'Posting…' : 'Post Announcement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard({ admin, onLogout }) {
  const [tab, setTab]             = useState('news');
  const [showPostNews, setShowPostNews]   = useState(false);
  const [editNews, setEditNews]           = useState(null);
  const [showPostAnn, setShowPostAnn]     = useState(false);
  const [showProfile, setShowProfile]     = useState(false);

  const { data: newsData } = useQuery(MY_CAMPUS_NEWS);
  const { data: annData  } = useQuery(ADMIN_ANNOUNCEMENTS);

  const [deleteNews]  = useMutation(DELETE_CAMPUS_NEWS,        { refetchQueries: ['MyCampusNews'] });
  const [pinNews]     = useMutation(PIN_CAMPUS_NEWS,           { refetchQueries: ['MyCampusNews'] });
  const [deleteAnn]   = useMutation(DELETE_ADMIN_ANNOUNCEMENT, { refetchQueries: ['AdminAnnouncements'] });

  const news  = newsData?.myCampusNews        || [];
  const anns  = annData?.adminAnnouncements   || [];

  const scope = admin.branch ? `${admin.branch} — ${admin.college}` : admin.college;
  const roleLabel = ROLES.find(r => r.value === admin.role)?.label || admin.role;

  return (
    <div className="space-y-5">
      {/* Admin header */}
      <div className="card p-5 flex items-start gap-4">
        <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center flex-none shadow">
          <Shield size={28} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold text-gray-900">{admin.name}</h2>
            <span className="badge bg-teal-100 text-teal-700 border-teal-200">{roleLabel}</span>
          </div>
          {admin.designation && <p className="text-sm text-gray-600">{admin.designation}</p>}
          <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5 flex-wrap">
            <span className="flex items-center gap-1"><Building2 size={11}/>{scope}</span>
            <span className="flex items-center gap-1">{admin.email}</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">Announcements &amp; news auto-scoped to: <strong className="text-gray-600">{scope}</strong></p>
        </div>
        <div className="flex flex-col gap-1.5 flex-none">
          <button onClick={() => setShowProfile(true)} className="btn-secondary text-xs flex items-center gap-1"><Edit2 size={12}/> Edit Profile</button>
          <button onClick={onLogout} className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 py-1"><LogOut size={12}/> Sign Out</button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'News Published', value: news.length,           icon: Newspaper, color: 'text-teal-600' },
          { label: 'Announcements',  value: anns.length,           icon: Megaphone, color: 'text-orange-500' },
          { label: 'Total Views',    value: [...news, ...anns].reduce((s,i)=>s+(i.views||0),0), icon: Eye, color: 'text-blue-600' },
        ].map(s => (
          <div key={s.label} className="card p-4 text-center">
            <s.icon size={22} className={`mx-auto mb-1 ${s.color}`} />
            <p className="text-2xl font-bold text-gray-900">{s.value}</p>
            <p className="text-xs text-gray-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {[
          { id: 'news', label: 'Campus News', icon: Newspaper },
          { id: 'ann',  label: 'Announcements', icon: Megaphone },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab===t.id ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <t.icon size={15}/>{t.label}
          </button>
        ))}
      </div>

      {/* News Tab */}
      {tab === 'news' && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button onClick={() => setShowPostNews(true)} className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-medium transition-colors">
              <Plus size={16}/> Post News
            </button>
          </div>
          {news.length === 0 ? (
            <div className="card p-12 text-center">
              <Newspaper size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500">No news posted yet</p>
              <button onClick={() => setShowPostNews(true)} className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-xl text-sm font-medium">Post First News</button>
            </div>
          ) : news.map(n => {
            const cfg = catCfg(n.category);
            const Icon = cfg.icon;
            return (
              <div key={n.id} className={`card p-4 ${n.pinned ? 'ring-1 ring-teal-300' : ''}`}>
                {n.pinned && <div className="flex items-center gap-1 text-xs text-teal-600 font-medium mb-2"><Pin size={12}/> Pinned</div>}
                <div className="flex items-start gap-3">
                  {n.imageUrl && (
                    <img src={n.imageUrl} alt="" className="w-20 h-14 rounded-lg object-cover flex-none" onError={e=>{e.target.style.display='none'}} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}><Icon size={11}/>{cfg.label}</span>
                      {n.branch && <span className="text-xs text-gray-400">{n.branch}</span>}
                    </div>
                    <h3 className="font-semibold text-gray-900 line-clamp-2">{n.title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                      <span className="flex items-center gap-0.5"><Eye size={11}/>{n.views}</span>
                      <span>{new Date(n.createdAt).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}</span>
                      {n.tags?.length > 0 && <span>{n.tags.map(t=>`#${t}`).join(' ')}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 flex-none">
                    <button onClick={() => pinNews({ variables: { newsId: n.id, pinned: !n.pinned } })}
                      className={`p-1.5 rounded-lg border text-xs transition-colors ${n.pinned ? 'text-teal-600 border-teal-300 bg-teal-50' : 'text-gray-400 border-gray-200 hover:border-teal-300'}`}
                      title={n.pinned ? 'Unpin' : 'Pin'}>
                      {n.pinned ? <PinOff size={14}/> : <Pin size={14}/>}
                    </button>
                    <button onClick={() => setEditNews(n)} className="p-1.5 rounded-lg border text-gray-400 border-gray-200 hover:text-blue-600 hover:border-blue-300 transition-colors">
                      <Edit2 size={14}/>
                    </button>
                    <button onClick={() => { if(window.confirm('Delete this news?')) deleteNews({ variables: { newsId: n.id } }); }}
                      className="p-1.5 rounded-lg border text-gray-400 border-gray-200 hover:text-red-600 hover:border-red-300 transition-colors">
                      <Trash2 size={14}/>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Announcements Tab */}
      {tab === 'ann' && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button onClick={() => setShowPostAnn(true)} className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-medium transition-colors">
              <Plus size={16}/> Post Announcement
            </button>
          </div>
          {anns.length === 0 ? (
            <div className="card p-12 text-center">
              <Megaphone size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500">No announcements posted yet</p>
              <button onClick={() => setShowPostAnn(true)} className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-medium">Post First Announcement</button>
            </div>
          ) : anns.map(a => {
            const tc = ANN_TYPES.find(t=>t.value===a.type) || ANN_TYPES[0];
            return (
              <div key={a.id} className={`card p-4 ${a.pinned ? 'ring-1 ring-orange-300' : ''}`}>
                {a.pinned && <div className="flex items-center gap-1 text-xs text-orange-600 font-medium mb-1.5"><Pin size={12}/> Pinned</div>}
                <div className="flex items-start gap-3">
                  <div className={`p-2.5 rounded-xl flex-none ${tc.color.split(' ')[1]} bg-opacity-30`}>
                    <Megaphone size={18} className={tc.color.split(' ')[0]} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tc.color}`}>{tc.label}</span>
                      {a.branch && <span className="text-xs text-gray-400">{a.branch}</span>}
                      {a.subject && <span className="text-xs text-gray-400">• {a.subject}</span>}
                    </div>
                    <h3 className="font-semibold text-gray-900">{a.title}</h3>
                    <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{a.body}</p>
                    <p className="text-xs text-gray-400 mt-1">{new Date(a.createdAt).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}</p>
                  </div>
                  <button onClick={() => { if(window.confirm('Delete?')) deleteAnn({ variables: { id: a.id } }); }}
                    className="p-1.5 rounded-lg border text-gray-400 border-gray-200 hover:text-red-600 hover:border-red-300 transition-colors flex-none">
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {showPostNews            && <PostNewsModal onClose={() => setShowPostNews(false)} />}
      {editNews                && <PostNewsModal existing={editNews} onClose={() => setEditNews(null)} />}
      {showPostAnn             && <PostAnnouncementModal onClose={() => setShowPostAnn(false)} />}
      {showProfile             && <EditProfileModal admin={admin} onClose={() => setShowProfile(false)} />}
    </div>
  );
}

// ── Edit Profile Modal ───────────────────────────────────────────────────────
function EditProfileModal({ admin, onClose }) {
  const [form, setForm] = useState({ name: admin.name, designation: admin.designation||'', avatarUrl: admin.avatarUrl||'' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [update, { loading }] = useMutation(UPDATE_ADMIN_PROFILE, { refetchQueries: ['MyAdminProfile'] });

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="font-bold text-gray-900">Edit Profile</h2>
          <button onClick={onClose}><X size={20} className="text-gray-400"/></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
            <input className="input" value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Designation</label>
            <input className="input" value={form.designation} onChange={e => set('designation', e.target.value)} placeholder="HOD-CSE, Dean, Principal…" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Avatar URL</label>
            <input className="input" value={form.avatarUrl} onChange={e => set('avatarUrl', e.target.value)} placeholder="https://…/photo.jpg" />
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button disabled={loading} onClick={async()=>{ try{await update({variables:{name:form.name,designation:form.designation||null,avatarUrl:form.avatarUrl||null}});onClose();}catch(e){alert(e.message);}}}
              className="flex-1 py-2 bg-teal-600 text-white rounded-xl font-medium">
              {loading ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Export ──────────────────────────────────────────────────────────────
export default function CollegeAdminPage() {
  const [token, setToken]   = useState(() => localStorage.getItem(ADMIN_KEY)  || null);
  const [admin, setAdmin]   = useState(() => { try { return JSON.parse(localStorage.getItem(ADMIN_DATA)||'null'); } catch { return null; } });
  const client = useApolloClient();

  const handleAuth = (tok, adm) => { setToken(tok); setAdmin(adm); };
  const handleLogout = () => {
    localStorage.removeItem(ADMIN_KEY);
    localStorage.removeItem(ADMIN_DATA);
    client.resetStore();
    setToken(null); setAdmin(null);
  };

  if (!token || !admin) return <AuthPanel onAuth={handleAuth} />;
  return <Dashboard admin={admin} onLogout={handleLogout} />;
}
